import { TemporalFramingConfig } from "../agent-config-options";

/**
 * Default master switch for the temporal-framing convention. When no
 * config is supplied the convention ships **enabled** so every
 * configulator-consuming repo's analyst agents apply the
 * "as of [date]" qualifier rule.
 *
 * @see TemporalFramingConfig
 */
export const DEFAULT_TEMPORAL_FRAMING_ENABLED = true;

/**
 * Default path globs the rule applies to — every Markdown file under
 * the profile / research subtrees of a repo's Starlight docs site.
 * Consumers may override the list when their content layout differs.
 *
 * Out-of-scope locations (meeting notes, requirements, the
 * project-context page) are excluded by design: their own dating
 * conventions (file-name date prefix, version frontmatter, living
 * snapshot under direct human review) already anchor the temporal
 * meaning of their content.
 *
 * @see TemporalFramingConfig
 */
export const DEFAULT_TEMPORAL_FRAMING_PATHS: ReadonlyArray<string> = [
  "docs/src/content/docs/profiles/**/*.md",
  "docs/src/content/docs/industry-research/**/*.md",
  "docs/src/content/docs/software-research/**/*.md",
  "docs/src/content/docs/regulatory-research/**/*.md",
  "docs/src/content/docs/standards-research/**/*.md",
  "docs/src/content/docs/customer-research/**/*.md",
];

/**
 * The five canonical time-sensitive claim categories surfaced by the
 * May 2026 sampled drift audit. The category names are shipped as the
 * key set for `TemporalFramingConfig.cadences` so consumers can dial
 * the per-category refresh cadence without inventing their own
 * category names.
 */
export const TEMPORAL_FRAMING_CATEGORY_VALUES = [
  "ownership",
  "company-leadership",
  "regulatory-status",
  "litigation",
  "dated-metrics",
] as const;

export type TemporalFramingCategory =
  (typeof TEMPORAL_FRAMING_CATEGORY_VALUES)[number];

/**
 * Default per-category refresh cadences (in days). Fast-decay claims
 * (regulatory status, litigation) carry a 30-day cadence because a
 * single press release can invalidate them between scheduled refresh
 * passes. Slow-decay claims (ownership, dated metrics from press
 * releases or filings) carry a 180-day cadence — material changes
 * still happen but rarely outpace a half-yearly refresh. Leadership
 * tenure sits in the middle at 90 days.
 *
 * Consumers may override any subset of categories via
 * `TemporalFramingConfig.cadences`; unspecified entries fall through
 * to these defaults.
 */
export const DEFAULT_TEMPORAL_FRAMING_CADENCES: {
  readonly [K in TemporalFramingCategory]: number;
} = {
  ownership: 180,
  "company-leadership": 90,
  "regulatory-status": 30,
  litigation: 30,
  "dated-metrics": 180,
};

/**
 * Default for whether the convention emits the
 * `.claude/procedures/check-temporal-framing.sh` lint script to disk.
 * Disabled by default — consumers opt in when they want a hard
 * pre-commit or CI gate. The rule body itself ships unconditionally
 * regardless of the lint script.
 *
 * @see TemporalFramingConfig
 */
export const DEFAULT_TEMPORAL_FRAMING_EMIT_CHECKER = false;

/**
 * Fully-resolved temporal-framing settings. Every field is defaulted
 * so downstream renderers can reason about a single canonical shape.
 */
export interface ResolvedTemporalFraming {
  readonly enabled: boolean;
  readonly paths: ReadonlyArray<string>;
  readonly cadences: { readonly [K in TemporalFramingCategory]: number };
  readonly emitChecker: boolean;
}

/**
 * Resolve a (possibly absent) `TemporalFramingConfig` into a canonical
 * `ResolvedTemporalFraming` with every field filled in. Unset fields
 * cascade from their documented defaults.
 *
 * Malformed configs throw a descriptive `Error`:
 *
 * - `paths` containing empty / whitespace-only entries.
 * - `cadences` containing non-integer or non-positive values.
 */
export function resolveTemporalFraming(
  config?: TemporalFramingConfig,
): ResolvedTemporalFraming {
  const paths = config?.paths ?? DEFAULT_TEMPORAL_FRAMING_PATHS;
  assertValidPaths(paths);

  const cadences = resolveCadences(config?.cadences);

  return {
    enabled: config?.enabled ?? DEFAULT_TEMPORAL_FRAMING_ENABLED,
    paths,
    cadences,
    emitChecker: config?.emitChecker ?? DEFAULT_TEMPORAL_FRAMING_EMIT_CHECKER,
  };
}

/**
 * Synth-time validation hook. Throws a descriptive `Error` when the
 * supplied `TemporalFramingConfig` is malformed. Called by
 * `AgentConfig.preSynthesize` before any rendering so a misconfigured
 * convention fails the build instead of silently shipping broken
 * paths or cadences. Returns the resolved config unchanged so callers
 * can write `const tf = validateTemporalFramingConfig(config)` in
 * one line.
 *
 * Malformed cases rejected here:
 *
 * - `paths` containing empty or whitespace-only entries.
 * - `cadences` containing non-integer or non-positive values.
 */
export function validateTemporalFramingConfig(
  config?: TemporalFramingConfig,
): ResolvedTemporalFraming {
  return resolveTemporalFraming(config);
}

/**
 * Render the body for the `temporal-framing-convention` rule shipped
 * by the `base` bundle. The rule documents:
 *
 * - The "as of [date]" qualifier requirement on time-sensitive claims.
 * - The five canonical time-sensitive claim categories.
 * - Refresh-agent behaviour (grep for `as of `, re-verify against the
 *   category-specific cadence).
 * - The scope of applicability (profile / research sections only).
 *
 * When the convention is disabled, the rule renders a short stub that
 * tells agents the project does not enforce explicit temporal
 * qualifiers and that staleness is caught by review alone.
 */
export function renderTemporalFramingRuleContent(
  tf: ResolvedTemporalFraming,
): string {
  if (!tf.enabled) {
    return [
      "# Temporal Framing Convention",
      "",
      "**Temporal framing is not enforced in this project.** Agents are",
      "not required to add `as of [date]` qualifiers to time-sensitive",
      "claims, and refresh passes do not use a mechanical grep to find",
      "stale framing. Staleness is caught by review and downstream user",
      "reports alone.",
      "",
      "Enable the convention via",
      "`AgentConfigOptions.temporalFraming.enabled = true`.",
    ].join("\n");
  }

  const lines: Array<string> = [
    "# Temporal Framing Convention",
    "",
    "Every factual claim that can change over time MUST carry an inline",
    "`as of [YYYY-MM-DD]` or `as of [Month YYYY]` qualifier on the",
    "**first** occurrence of the claim in the document. Once qualified",
    "once, subsequent references to the same fact in the same document",
    "do not need to repeat the qualifier — the first qualifier governs",
    "the document's snapshot date for that fact.",
    "",
    "The qualifier is mechanical, not editorial. Refresh agents grep",
    "for `as of ` to find the time-bounded claims; without the",
    "qualifier, a refresh pass has no cheap way to tell stale framing",
    "from stable facts.",
    "",
    "## What counts as time-sensitive",
    "",
    "A claim is time-sensitive (and must carry the qualifier) when it",
    "describes a state that **a single public event could invalidate",
    "within the document's expected refresh cadence**. Five recurring",
    "categories cover the common cases:",
    "",
    "| Category | Examples |",
    "|----------|----------|",
    '| **Ownership and investors** | "co-owned by X, Y, Z"; "majority-owned by"; "minority stake held by"; cap table; current investor lists |',
    '| **Leadership tenure** | "X is CEO"; "X chairs the committee"; "X serves as"; current board chair; current executive team |',
    '| **Regulatory or rule status** | "is in force"; "under public consultation"; "pending finalization"; "currently enjoined"; "under active investigation" |',
    '| **Litigation and enforcement** | "is under active scrutiny"; "investigation is ongoing"; "appeal is pending"; "consent decree remains in effect" |',
    '| **Pricing, valuation, and metrics** | "valued at $XB"; "annual revenue ~$X"; "processes N transactions"; "serves N customers" — when the figure is sourced from a dated press release or filing |',
    "",
    "A claim is **not** time-sensitive (and does not need the qualifier)",
    "when it describes a historical event, a statutory text, or a",
    "structural fact that does not change without amendment. Examples",
    "that are safe without a qualifier:",
    "",
    '- "Acquired by Advent International in May 2019" (a dated historical event — the date itself is the anchor)',
    '- "21 CFR Part 11 establishes criteria for electronic records" (statutory text)',
    '- "Headquartered in Dallas, Texas" (changes only on a documented move)',
    '- "Founded in 1999" (a one-time event)',
    "",
    "## Format",
    "",
    "Two acceptable forms:",
    "",
    "```markdown",
    "As of May 2026, Acme is owned by ...",
    "... Acme's CEO (as of May 2026) is ...",
    "```",
    "",
    "Use the granularity that matches the source citation:",
    "",
    "- `as of YYYY-MM-DD` when the source is a specific filing, press release, or court order with a precise date.",
    "- `as of Month YYYY` when the source is a self-reported page (e.g., a company's `About` page) where only the month-of-snapshot is meaningful.",
    "- Do **not** write `as of 2026` (year-only) — that's too coarse to drive a refresh decision.",
    "",
    "## Examples",
    "",
    "### Wrong — present-tense framing of a state that can change",
    "",
    "```markdown",
    "Availity is co-owned by major Blue Cross Blue Shield plans",
    "(HCSC, Elevance Health/Anthem, BCBS Minnesota), Francisco",
    "Partners, Novo Holdings, and Prettybrook Partners.",
    "```",
    "",
    "This claim might have been true at a specific date but goes stale",
    "as soon as any one investor exits. A refresh agent has no",
    "mechanical signal that the claim is time-sensitive.",
    "",
    "### Right — explicit qualifier on the first claim",
    "",
    "```markdown",
    "As of May 2026, Availity is co-owned by a mix of health plan",
    "strategic investors (HCSC, Elevance Health/Anthem, BCBS",
    "Minnesota, Humana, GuideWell) and financial sponsors (Novo",
    "Holdings, Prettybrook Partners).",
    "```",
    "",
    "The `as of May 2026` anchors the document's snapshot for the",
    "ownership claim. Subsequent references to Availity's ownership in",
    "the same document inherit that snapshot date.",
    "",
    "## When the convention applies",
    "",
    "The rule applies to every Markdown document any agent writes under",
    "the following path globs:",
    "",
    ...tf.paths.map((p) => `- \`${p}\``),
    "",
    "It does **not** apply to:",
    "",
    "- Meeting notes (`meetings/**`) — the meeting date itself is the implicit `as of` anchor.",
    "- Requirement documents (`requirements/**`) — formal requirements are versioned and dated via their own frontmatter.",
    "- The project context page (`project-context.md`) — maintained as a living snapshot under direct human review.",
    "",
    "## Refresh-agent behaviour",
    "",
    "A refresh pass on any covered document MUST:",
    "",
    "1. Grep for `as of ` (case-insensitive) in the document.",
    "2. For each qualifier, compute `today - <qualifier-date>` in days.",
    "3. Classify each qualified claim against the category-specific",
    "   cadence table below.",
    "4. Re-verify every claim whose qualifier is older than its cadence.",
    "5. Update the qualifier to today's date on every re-verified claim",
    "   that remains accurate.",
    "6. Treat documents that omit qualifiers entirely as needing",
    "   **back-fill** before any other verification is attempted — the",
    "   absence of qualifiers is itself a refresh finding.",
    "",
    "### Refresh cadence table",
    "",
    "Each category carries its own refresh cadence in days. Faster-decay",
    "claims (regulatory status, litigation) refresh more often;",
    "slower-decay claims (ownership, dated metrics) refresh less often.",
    "",
    "| Category | Cadence (days) |",
    "|----------|----------------|",
    `| \`ownership\` | ${tf.cadences.ownership} |`,
    `| \`company-leadership\` | ${tf.cadences["company-leadership"]} |`,
    `| \`regulatory-status\` | ${tf.cadences["regulatory-status"]} |`,
    `| \`litigation\` | ${tf.cadences.litigation} |`,
    `| \`dated-metrics\` | ${tf.cadences["dated-metrics"]} |`,
    "",
    "Consumers may override any subset of cadences via",
    "`AgentConfigOptions.temporalFraming.cadences`. Unspecified",
    "categories fall through to the defaults above.",
  ];

  if (tf.emitChecker) {
    lines.push(
      "",
      "## Lint",
      "",
      "The bundled `.claude/procedures/check-temporal-framing.sh` helper",
      "enforces the rule mechanically. Given a set of changed files (as",
      "positional arguments or a newline-separated list on stdin), it",
      "fails non-zero when any file matches a configured path glob and",
      "carries time-sensitive framing without an `as of ` qualifier.",
      "",
      "```bash",
      "# Pre-commit (staged files):",
      "git diff --cached --name-only \\",
      "  | .claude/procedures/check-temporal-framing.sh",
      "",
      "# CI (branch-vs-base):",
      "git diff --name-only origin/main...HEAD \\",
      "  | .claude/procedures/check-temporal-framing.sh",
      "```",
      "",
      "The lint is a coarse signal — it flags files that look",
      "time-sensitive but carry no qualifier. Authors and reviewers are",
      "still expected to validate that each qualifier is correctly",
      "anchored to its source citation.",
    );
  }

  lines.push(
    "",
    "## Out of scope",
    "",
    "- Auto-generating qualifiers from source-citation dates. Authors",
    "  read the source and write the qualifier explicitly.",
    "- Replacing existing dated frontmatter conventions. Frontmatter",
    "  `date:` fields anchor the document's overall snapshot; the",
    "  inline qualifier anchors individual time-sensitive claims so a",
    "  refresh pass can re-verify them without re-reading the whole",
    "  document.",
    "- Back-filling pre-existing documents in bulk. Refresh passes",
    "  back-fill qualifiers on the documents they touch; bulk",
    "  back-fill is a downstream consumer cleanup task, not a",
    "  configulator change.",
  );

  return lines.join("\n");
}

/**
 * Render the `.claude/procedures/check-temporal-framing.sh` helper
 * script. Exported so `AgentConfig` can register it when the consumer
 * opts in via `emitChecker: true`.
 *
 * The script accepts the list of changed files as either:
 *
 * 1. Positional arguments (one file per arg).
 * 2. Newline-separated entries on stdin (when no args supplied) —
 *    pipe `git diff --name-only` directly into it.
 *
 * It fails non-zero when any changed file matches a configured path
 * pattern and contains time-sensitive framing (present-tense forms of
 * the canonical category triggers) but lacks an `as of ` qualifier
 * anywhere in the file. The lint is intentionally coarse — file-level
 * not line-level — so the cost of running it on every PR stays low.
 */
export function renderTemporalFramingCheckerScript(
  tf: ResolvedTemporalFraming,
): string {
  const patternsLiteral = tf.paths
    .map((p) => `  ${JSON.stringify(p)}`)
    .join("\n");

  return [
    "#!/usr/bin/env bash",
    "# check-temporal-framing.sh — Enforce the temporal-framing convention.",
    "#",
    "# Usage:",
    "#   .claude/procedures/check-temporal-framing.sh <file>...",
    "#   git diff --name-only HEAD | .claude/procedures/check-temporal-framing.sh",
    "#",
    "# Fails non-zero when any changed file matches a configured path",
    "# pattern AND contains present-tense framing of a time-sensitive",
    "# claim category AND does NOT carry an `as of ` qualifier anywhere",
    "# in the file. The lint is file-level, not line-level — the cheap",
    "# coarse signal is enough to catch the failure mode the convention",
    "# targets, and a finer-grained check would carry too much false",
    "# positive cost.",
    "#",
    "# The path patterns are rendered from the consumer's",
    "# TemporalFramingConfig at synth time — do not edit by hand;",
    "# regenerate via `pnpm exec projen`.",
    "#",
    "# Patterns support the `**` recursive-segment wildcard even though",
    "# bash's native `[[ $file == $glob ]]` pattern matching does not:",
    "# each glob is converted to a POSIX-extended regex internally and",
    "# matched with `=~`, so the script works uniformly on bash 3.2",
    "# (macOS default) and bash 4+ (Linux / CI).",
    "",
    "set -uo pipefail",
    "",
    "err() {",
    '  printf "check-temporal-framing.sh: %s\\n" "$*" >&2',
    "}",
    "",
    "# Path glob patterns. Translated to anchored POSIX-extended regexes",
    "# at runtime by `glob_to_regex` below so `**` matches recursively",
    "# across path segments.",
    "path_patterns=(",
    patternsLiteral,
    ")",
    "",
    "# Triggers — present-tense framing keywords that indicate a",
    "# time-sensitive claim category. Match is case-insensitive and",
    "# whole-word; the keyword list is deliberately small (one or two",
    "# canonical phrases per category) to keep false positives low.",
    "triggers=(",
    '  "is owned by"',
    '  "co-owned by"',
    '  "majority-owned by"',
    '  "minority-owned by"',
    '  "is co-owned"',
    '  "is currently"',
    '  "currently chairs"',
    '  "currently serves"',
    '  "is in force"',
    '  "under public consultation"',
    '  "under active investigation"',
    '  "is pending"',
    '  "investigation is ongoing"',
    ")",
    "",
    "# Convert a bash-style glob into an anchored POSIX-extended regex:",
    "#   **/   → (.+/)?   (zero or more path segments plus trailing slash)",
    "#   **    → .*       (anywhere, including `/`)",
    "#   *     → [^/]*    (single path segment)",
    "#   ?     → [^/]     (single non-slash char)",
    "glob_to_regex() {",
    '  local glob="$1"',
    '  local regex=""',
    "  local i=0",
    "  local len=${#glob}",
    "  while (( i < len )); do",
    '    local c="${glob:i:1}"',
    '    if [[ "$c" == "*" ]]; then',
    '      local next="${glob:i+1:1}"',
    '      if [[ "$next" == "*" ]]; then',
    '        local after="${glob:i+2:1}"',
    '        if [[ "$after" == "/" ]]; then',
    '          regex+="(.+/)?"',
    "          i=$((i+3))",
    "          continue",
    "        fi",
    '        regex+=".*"',
    "        i=$((i+2))",
    "        continue",
    "      fi",
    '      regex+="[^/]*"',
    "      i=$((i+1))",
    "      continue",
    "    fi",
    '    if [[ "$c" == "?" ]]; then',
    '      regex+="[^/]"',
    "      i=$((i+1))",
    "      continue",
    "    fi",
    '    case "$c" in',
    "      .|+|\\(|\\)|\\[|\\]|\\{|\\}|\\||\\^|\\$|\\\\)",
    '        regex+="\\\\$c"',
    "        ;;",
    "      *)",
    '        regex+="$c"',
    "        ;;",
    "    esac",
    "    i=$((i+1))",
    "  done",
    '  printf "^%s$" "$regex"',
    "}",
    "",
    "# Check whether $file matches any of the configured path patterns.",
    "# Returns 0 on match, 1 otherwise.",
    "matches_any_pattern() {",
    '  local file="$1"',
    "  local pattern regex",
    '  for pattern in "${path_patterns[@]}"; do',
    '    regex=$(glob_to_regex "$pattern")',
    '    if [[ "$file" =~ $regex ]]; then',
    "      return 0",
    "    fi",
    "  done",
    "  return 1",
    "}",
    "",
    "# Check whether $file contains any present-tense framing trigger",
    "# without an `as of ` qualifier anywhere in the file. Returns 0",
    "# when the file is OK (no triggers, or triggers + qualifier), 1",
    "# when the file is malformed (triggers + no qualifier).",
    "file_ok() {",
    '  local file="$1"',
    '  [[ ! -f "$file" ]] && return 0',
    "  local found_trigger=0",
    "  local trigger",
    '  for trigger in "${triggers[@]}"; do',
    '    if grep -qi -- "$trigger" "$file"; then',
    "      found_trigger=1",
    "      break",
    "    fi",
    "  done",
    "  if [[ $found_trigger -eq 0 ]]; then",
    "    return 0",
    "  fi",
    '  if grep -qi -- "as of " "$file"; then',
    "    return 0",
    "  fi",
    "  return 1",
    "}",
    "",
    "# Read the file list from positional args or stdin.",
    "files=()",
    "if [[ $# -gt 0 ]]; then",
    '  files=("$@")',
    "else",
    "  while IFS= read -r line; do",
    '    [[ -n "$line" ]] && files+=("$line")',
    "  done",
    "fi",
    "",
    "if [[ ${#files[@]} -eq 0 ]]; then",
    "  exit 0",
    "fi",
    "",
    "violations=0",
    'for file in "${files[@]}"; do',
    '  if ! matches_any_pattern "$file"; then',
    "    continue",
    "  fi",
    '  if ! file_ok "$file"; then',
    '    err "$file: contains time-sensitive framing without an \\"as of\\" qualifier"',
    "    violations=$((violations+1))",
    "  fi",
    "done",
    "",
    "if [[ $violations -gt 0 ]]; then",
    '  err "found $violations file(s) violating the temporal-framing convention"',
    "  exit 1",
    "fi",
    "exit 0",
  ].join("\n");
}

/*******************************************************************************
 *
 * Internal helpers
 *
 ******************************************************************************/

function assertValidPaths(paths: ReadonlyArray<string>): void {
  if (!Array.isArray(paths)) {
    throw new Error(
      `TemporalFramingConfig.paths must be an array; got ${typeof paths}`,
    );
  }
  for (let i = 0; i < paths.length; i++) {
    const entry = paths[i];
    if (typeof entry !== "string" || entry.trim().length === 0) {
      throw new Error(
        `TemporalFramingConfig.paths[${i}] must be a non-empty string; got ${JSON.stringify(
          entry,
        )}`,
      );
    }
  }
}

function resolveCadences(
  supplied?: Partial<{ readonly [K in TemporalFramingCategory]: number }>,
): { readonly [K in TemporalFramingCategory]: number } {
  const merged: { [K in TemporalFramingCategory]: number } = {
    ...DEFAULT_TEMPORAL_FRAMING_CADENCES,
  };

  if (supplied !== undefined) {
    for (const category of TEMPORAL_FRAMING_CATEGORY_VALUES) {
      const value = supplied[category];
      if (value === undefined) {
        continue;
      }
      assertValidCadence(value, category);
      merged[category] = value;
    }
  }

  return merged;
}

function assertValidCadence(value: number, field: string): void {
  if (typeof value !== "number" || !Number.isInteger(value)) {
    throw new Error(
      `TemporalFramingConfig.cadences.${field} must be a positive integer; got ${value}`,
    );
  }
  if (value <= 0) {
    throw new Error(
      `TemporalFramingConfig.cadences.${field} must be a positive integer; got ${value}`,
    );
  }
}
